import React, { useEffect, useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Sun, Moon, ShieldAlert, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [resendOn, setResendOn] = useState(false);

  useEffect(() => {
    api.get("/users/me/email-prefs").then((r) => {
      setPrefs(r.data.email_prefs);
      setResendOn(!!r.data.resend_enabled);
    });
  }, []);

  const save = async (patch) => {
    try {
      const r = await api.patch("/users/me/email-prefs", patch);
      setPrefs(r.data.email_prefs);
      toast.success("Email preferences updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="container py-10 max-w-2xl">
      <p className="eyebrow mb-1">Settings</p>
      <h1 className="display-2 mb-8">Make KARM yours.</h1>

      <section className="vital-card-static p-6 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold">Night mode</h3>
            <p className="text-sm text-muted-foreground">Easier on the eyes after hours.</p>
          </div>
          <div className="flex items-center gap-3">
            {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            <Switch
              checked={theme === "dark"}
              onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
              data-testid="settings-theme-switch"
            />
          </div>
        </div>
      </section>

      <section className="vital-card-static p-6 mb-4" data-testid="settings-email-prefs">
        <h3 className="font-bold mb-1 inline-flex items-center gap-2"><Mail className="h-4 w-4" /> Email notifications</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {resendOn ? "Live · powered by Resend" : "Currently in DRY-RUN — no emails are actually sent."}
        </p>
        {prefs && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-semibold">Job digest</p>
                <p className="text-xs text-muted-foreground">Fresh GCC roles matching your skills / location.</p>
              </div>
              <Select value={prefs.job_digest} onValueChange={(v) => save({ job_digest: v })}>
                <SelectTrigger className="w-40" data-testid="pref-digest"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="daily">Daily (8am IST)</SelectItem>
                  <SelectItem value="weekly">Weekly (Mon 8am)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {[
              ["application_receipt", "Application confirmations", "When you apply, we email you a receipt with the company link."],
              ["subscription_receipts", "Subscription receipts", "Payments, renewals, plan changes."],
              ["marketing", "Product updates", "New features, partner GCC drops, occasional announcements."],
            ].map(([key, title, desc]) => (
              <div key={key} className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-semibold">{title}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
                <Switch
                  checked={!!prefs[key]}
                  onCheckedChange={(v) => save({ [key]: v })}
                  data-testid={`pref-${key}`}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="vital-card-static p-6 mb-4">
        <h3 className="font-bold mb-2">Account</h3>
        <div className="text-sm text-muted-foreground mb-4">
          {user?.email} · {user?.auth_provider} · role: {user?.role}
        </div>
        <Button variant="outline" onClick={() => navigate("/profile")} data-testid="settings-edit-profile">Edit Profile</Button>
      </section>

      <section className="vital-card-static p-6 border-destructive/30">
        <h3 className="font-bold mb-2 inline-flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-destructive" /> Sign out</h3>
        <p className="text-sm text-muted-foreground mb-4">End your session on this device.</p>
        <Button variant="destructive" onClick={async () => { await logout(); navigate("/"); }} data-testid="settings-logout">Sign out</Button>
      </section>
    </div>
  );
}
